import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { Modal } from '@/components/shared/modal/Modal';
import { handleMutationResult } from '@/helpers/toastHelper';
import { SkillType } from '@/services/tasks/skillSettings.types';
import { useCreateSkills, useSkills } from '@/services/tasks/useSkills';
import { strings } from '@/services/translation/strings';
import { FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../admin.translations';

interface AddSkillFormValues {
  name: string;
}

interface AdminAddSkillModalProps {
  open: boolean;
  onClose: () => void;
}

const AdminAddSkillModal: FunctionComponent<AdminAddSkillModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();

  const { fetching, create } = useCreateSkills();

  const methods = useForm<AddSkillFormValues>();

  const skills = useSkills();

  const onSubmit = async (values: AddSkillFormValues) => {
    const newSkill: SkillType = {
      id: '',
      name: values.name,
      tooltip: '',
      isActive: true,
      displayName: `${strings.settings.manageSkills.displayName} ${skills.length + 1}`
    };

    const result = await create([newSkill]);

    await handleMutationResult(result, {
      successMessage: strings.common.success,
      errorMessage: strings.common.error,
      customSuccessCheck: data => !!data?.createSkills,
      onSuccess: () => {
        methods.reset();
        if (result.data?.createSkills) {
          const newSkillId = result.data?.createSkills?.[0].id;
          navigate(`${newSkillId}`, { state: { skill: result.data.createSkills[0] } });
        }
      }
    });
  };

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} header={adminStrings.skillsSection.addDialog.title}>
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <TextField
              label={adminStrings.skillsSection.addDialog.newSkill}
              name="name"
              id="name"
              placeholder={adminStrings.skillsSection.addDialog.skillName}
              customValidatorMessage={methods.formState.errors.name?.message}
              required
              rules={{
                required: adminStrings.skillsSection.addDialog.nameRequired,
                validate: (value: string) => {
                  const isDuplicate = skills?.some(s => s?.name === value);
                  if (isDuplicate) return adminStrings.skillsSection.addDialog.uniqueName;
                  return true;
                }
              }}
            />

            <div className="flex items-center justify-end gap-3">
              <SubmitCancelButtons
                disabled={!!methods.formState.errors.name?.message}
                onComplete={handleClose}
                loading={fetching}
                submitText={strings.common.create}
              />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </Modal>
  );
};

export default AdminAddSkillModal;
